import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { uploadDocument } from '@cover/core/api/client.ts';
import type { Document, DocumentClass, Source } from '@cover/core/api/types.ts';
import { Button, type ButtonProps } from '@cover/core/ui/Button.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { useBusinessId } from '../session.tsx';

const MAX = 16 * 1024 * 1024;

/** A button that picks a file and uploads it as a document. */
export function UploadButton({
  label,
  accept = 'image/*,application/pdf,.heic,.csv,.eml',
  source = 'dashboard',
  hint,
  capture,
  onUploaded,
  ...button
}: { label: string; accept?: string; source?: Source; hint?: DocumentClass; capture?: boolean; onUploaded?: (d: Document) => void | Promise<void> } & Omit<ButtonProps, 'onClick'>) {
  const input = useRef<HTMLInputElement>(null);
  const b = useBusinessId();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function onFile(file: File | undefined) {
    if (!file) return;
    if (file.size > MAX) return toast.fail(new Error('That file is over 16 MB. Try a smaller photo or PDF.'));
    setBusy(true);
    try {
      const doc = await uploadDocument(b, file, { source, hint });
      await onUploaded?.(doc);
    } catch (e) {
      toast.fail(e);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <>
      <input ref={input} type="file" accept={accept} capture={capture ? 'environment' : undefined} className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => onFile(e.target.files?.[0])} />
      <Button type="button" variant="secondary" {...button} busy={busy} onClick={() => input.current?.click()}>
        {!busy && <Upload className="h-4 w-4" aria-hidden />}
        {label}
      </Button>
    </>
  );
}
